import { useEffect, useState, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { AppleIcon } from '@/components/icons/apple';
import CheckIcon from '@/components/icons/check-icon';
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  getMobileStore,
  type MobileStore,
} from '@/lib/constants/app-stores';

const BENEFITS = [
  {
    title: 'Pedidos em poucos toques',
    text: 'Encontre medicamentos, dermocosméticos e genéricos com a mesma loja que você já usa no site.',
  },
  {
    title: 'Receita na palma da mão',
    text: 'Envie a prescrição pelo app e acompanhe a análise sem fila de WhatsApp.',
  },
  {
    title: 'Ofertas e programas',
    text: 'Acesse descontos de laboratórios e campanhas exclusivas direto no celular.',
  },
  {
    title: 'Entrega onde você estiver',
    text: 'Rastreie o pedido e receba em casa, com o mesmo cuidado da farmácia Qualidoc.',
  },
] as const;

function PlayBadgeIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path
        d="M3.6 2.3c-.4.2-.6.7-.6 1.3v16.8c0 .6.2 1.1.6 1.3l10.2-9.7L3.6 2.3Z"
        fill="#EA4335"
      />
      <path
        d="M16.9 11.3 13.2 7.6 3.6 2.3l10.2 9.7 3.1-.7Z"
        fill="#FBBC04"
      />
      <path
        d="M3.6 21.7 13.8 12l3.1.7-10.3 9.6c-.9.5-2 .1-3-.6Z"
        fill="#34A853"
      />
      <path
        d="M20.4 10.4c.8.5.8 1.7 0 2.2l-3.5 2-3.1-.7 3.7-3.7 2.9.2Z"
        fill="#4285F4"
      />
    </svg>
  );
}

function StoreButton({
  href,
  label,
  storeName,
  icon,
  recommended,
}: {
  href: string;
  label: string;
  storeName: string;
  icon: ReactNode;
  recommended: boolean;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${label} ${storeName}`}
      className={`group relative flex min-h-[56px] min-w-[220px] items-center gap-3 rounded-2xl px-5 py-3 text-left text-white shadow-lg transition-[transform,background-color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b3a6a] ${
        recommended
          ? 'bg-[#041e38] hover:bg-black'
          : 'bg-white/10 ring-1 ring-white/25 hover:bg-white/20'
      }`}
    >
      {recommended ? (
        <span className="absolute -top-2.5 right-3 rounded-full bg-[#f5c451] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#041e38]">
          Para o seu celular
        </span>
      ) : null}
      <span className="flex h-8 w-8 items-center justify-center">{icon}</span>
      <span className="flex flex-col leading-tight">
        <span className="text-[11px] font-medium text-white/80">{label}</span>
        <span className="text-base font-bold">{storeName}</span>
      </span>
    </a>
  );
}

function AppPhoneMockup() {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.div
      className="relative mx-auto w-[260px] shrink-0 lg:mx-0"
      aria-hidden="true"
      animate={shouldReduceMotion ? undefined : { y: [0, -10, 0] }}
      transition={
        shouldReduceMotion
          ? undefined
          : { duration: 6, repeat: Infinity, ease: 'easeInOut' }
      }
    >
      <div
        aria-hidden="true"
        className="absolute -inset-10 rounded-full bg-sky-300/30 blur-3xl"
      />
      <div className="relative rounded-[2.4rem] border-[10px] border-[#07182c] bg-[#07182c] shadow-[0_40px_80px_-24px_rgba(4,30,56,0.7)]">
        <div className="absolute left-1/2 top-2.5 z-10 h-5 w-[88px] -translate-x-1/2 rounded-full bg-black" />
        <div className="overflow-hidden rounded-[1.75rem] bg-gradient-to-b from-[#1a8ef0] to-[#0b3a6a] px-4 pb-5 pt-10 text-white">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/70">
            Qualidoc
          </p>
          <p className="mt-1 text-lg font-bold leading-snug">
            Sua farmácia no bolso
          </p>
          <div className="mt-4 space-y-2.5">
            {['Dipirona 500mg', 'Protetor solar FPS 60', 'Vitamina D 7.000'].map(
              (item) => (
                <div
                  key={item}
                  className="flex items-center justify-between rounded-xl bg-white/15 px-3 py-2.5 backdrop-blur-sm"
                >
                  <span className="text-xs font-medium">{item}</span>
                  <span className="text-[10px] font-semibold text-[#f5c451]">
                    Comprar
                  </span>
                </div>
              ),
            )}
          </div>
          <div className="mt-4 rounded-xl bg-white p-3 text-[#0b3a6a]">
            <p className="text-[11px] font-semibold">Pedido a caminho</p>
            <p className="mt-0.5 text-[10px] text-[#4b6280]">
              Previsão hoje, 16h–18h
            </p>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#d7e8f8]">
              <div className="h-full w-2/3 rounded-full bg-accent" />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function DownloadAppView() {
  const [preferredStore, setPreferredStore] = useState<MobileStore | null>(
    null,
  );

  useEffect(() => {
    setPreferredStore(
      getMobileStore(navigator.userAgent || navigator.vendor || ''),
    );
  }, []);

  return (
    <div className="bg-[#f4f7fb]">
      <section className="relative overflow-hidden bg-[#0b3a6a] text-white">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(800px 400px at 10% 0%, rgba(25,127,219,0.55), transparent 60%), radial-gradient(600px 320px at 90% 80%, rgba(245,196,81,0.18), transparent 55%)',
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />

        <div className="relative mx-auto flex w-full max-w-6xl flex-col items-center gap-12 px-4 py-14 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8 lg:py-20">
          <div className="max-w-xl text-center lg:text-left">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#f5c451]">
              App Qualidoc
            </p>
            <h1 className="mt-3 text-pretty text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl lg:leading-[1.1]">
              Farmácia completa, agora no seu celular
            </h1>
            <p className="mt-4 text-pretty text-base leading-relaxed text-white/80 sm:text-lg">
              Peça medicamentos, envie a receita e acompanhe a entrega com a
              mesma Qualidoc de sempre — grátis na App Store e no Google Play.
            </p>

            <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center lg:justify-start">
              <StoreButton
                href={APP_STORE_URL}
                label="Baixar na"
                storeName="App Store"
                recommended={preferredStore === 'ios'}
                icon={<AppleIcon className="h-7 w-7" />}
              />
              <StoreButton
                href={PLAY_STORE_URL}
                label="Disponível no"
                storeName="Google Play"
                recommended={preferredStore === 'android'}
                icon={<PlayBadgeIcon className="h-7 w-7" />}
              />
            </div>
            <p className="mt-4 text-sm text-white/65">
              iPhone, iPad e Android. Sem custo para instalar.
            </p>
          </div>

          <AppPhoneMockup />
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <h2 className="text-pretty text-2xl font-bold text-heading sm:text-3xl">
          Por que baixar o app
        </h2>
        <ul className="mt-8 grid gap-4 sm:grid-cols-2">
          {BENEFITS.map((benefit) => (
            <li
              key={benefit.title}
              className="flex gap-3 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-black/5"
            >
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#e8f3fc] text-accent">
                <CheckIcon width={16} height={16} aria-hidden="true" />
              </span>
              <div>
                <h3 className="font-bold text-heading">{benefit.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-body">
                  {benefit.text}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
